import { Button, ContentSwitcher, Dropdown, InlineNotification, Modal, Switch } from '@carbon/react';
import { Add, TrashCan, Undo } from '@carbon/icons-react';
import { AddressFields, EMPTY_ADDRESS, type AddressSource, type AddressValue } from '@pos/design-system';
import { useTranslation } from '@pos/i18n';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useAdmin, useResourceApi, type FoundAddress } from './adminContext';
import { FormModal } from './components/FormModal';
import { ListTable, type ListColumn } from './components/ListTable';
import { PageHeader } from './components/PageHeader';
import { RowAction } from './components/RowAction';
import { CellValue } from './fields/CellValue';
import { FieldInput } from './fields/FieldInput';
import { matchDivision } from './fields/placeMatch';
import { AddressLookupField } from './fields/AddressLookupField';
import { missingRequired, requiredMarking } from './fields/required';
import { FilterLayout } from './filters/FilterPanel';
import { ResourceFilters } from './filters/ResourceFilters';
import { useFilters, withValues } from './filters/useFilters';
import { asProblem, problemMessage } from './problems';
import { formValues, recordLabel, type AddressFieldKeys, type RecordValues, type ResourceDef } from './resources';
import { hasPermission } from './session';
import './ResourcePage.scss';

export interface ResourcePageProps {
  def: ResourceDef;
  /** For a child resource: the parent record's id. */
  parentId?: string;
  /** Opens a record's own page (resources with children); otherwise rows edit in a dialog. */
  onOpen?(record: RecordValues): void;
  /** Opens the page that creates a record; otherwise *Create* opens a dialog. */
  onCreate?(): void;
  /** Hide the page title, e.g. inside a tab that already names it. */
  embedded?: boolean;
  /** Extra buttons in the list's toolbar, before *Create*; `refresh` reloads the list after they change it. */
  toolbar?(list: { refresh(): void }): ReactNode;
  /** One-click filters shown as a switcher above the list, after *All*; each sets the filters it names. Omitted: no switcher. */
  quickFilters?: readonly { label: string; values: Readonly<Record<string, string>> }[];
  /**
   * Values every row of this list has, e.g. `{ contactId }` on a contact's tab (`tabOf`): the list is filtered by them,
   * new rows get them, and their fields, columns and filters are not shown.
   */
  fixed?: Readonly<Record<string, string>>;
}

const PAGE_SIZES = [25, 50, 100];

/** The filter that shows the removed records instead of the current ones (ADR 0117), last in the filter panel. */
const REMOVED = 'removed';

/**
 * List, search, create, edit and delete for one generated resource. Edits carry the record's version; if someone
 * else changed it meanwhile, the save fails with a clear message instead of overwriting their change.
 */
/** *Show*: the current records (the default) or the removed ones, to restore one (ADR 0117). */
function RemovedFilter({ value, onChange }: { value: string; onChange(value: string): void }) {
  const { t } = useTranslation();
  const items = [
    { id: '', label: t('admin.removed.current') },
    { id: 'true', label: t('admin.removed.view') },
  ];
  return (
    <Dropdown<{ id: string; label: string }>
      id="filter-removed"
      titleText={t('admin.removed.show')}
      label={t('admin.removed.current')}
      items={items}
      itemToString={(o) => o?.label ?? ''}
      // null (not undefined) keeps it controlled; with nothing chosen it reads *Current records*, as after Reset.
      selectedItem={(items.find((o) => o.id && o.id === value) ?? null) as { id: string; label: string }}
      onChange={({ selectedItem }) => onChange(selectedItem?.id ?? '')}
    />
  );
}

export function ResourcePage({
  def,
  parentId,
  onOpen,
  onCreate,
  embedded = false,
  toolbar,
  quickFilters,
  fixed,
}: ResourcePageProps) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const api = useResourceApi(def, parentId);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const isFixed = (key: string) => Boolean(fixed && key in fixed);
  const filterDefs = (def.filters ?? []).filter((f) => !isFixed(f.key));
  const formDef = fixed
    ? { ...def, fields: def.fields.map((f) => (isFixed(f.key) ? { ...f, hidden: true } : f)) }
    : def;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0] ?? 25);
  const filters = useFilters(
    Object.fromEntries([...filterDefs.map((f) => [f.key, '']), [REMOVED, '']]),
    () => setPage(1),
  );
  const [editing, setEditing] = useState<{ record?: RecordValues; values: Record<string, unknown> }>();
  const [deleting, setDeleting] = useState<RecordValues>();
  // The removed records, to restore one (ADR 0117): a filter in the side panel, sent as `removed=true`.
  const showRemoved = filters.applied[REMOVED] === 'true';
  const [restoring, setRestoring] = useState<RecordValues>();
  const [submitted, setSubmitted] = useState(false);
  const { addressSource } = useAdmin();
  const canWrite = Boolean(def.writePermission) && hasPermission(session, def.writePermission ?? undefined);

  const list = useQuery({
    queryKey: [api.path, fixed, search, filters.applied, page, pageSize],
    queryFn: () =>
      api.list({
        ...withValues(filters.applied),
        ...fixed,
        search: search || undefined,
        page,
        pageSize,
      }),
    placeholderData: keepPreviousData,
  });

  // The switcher shows a quick filter while exactly its values are applied, *All* while no filter is.
  const appliedEntries = Object.entries(withValues(filters.applied));
  const quickMatch = (values: Readonly<Record<string, string>>) =>
    appliedEntries.length === Object.keys(values).length && appliedEntries.every(([k, v]) => values[k] === v);
  const quickIndex =
    appliedEntries.length === 0 ? 0 : Math.max(0, (quickFilters?.findIndex((q) => quickMatch(q.values)) ?? -1) + 1);

  const refresh = () => queryClient.invalidateQueries({ queryKey: [api.path] });

  const save = useMutation({
    mutationFn: ({ record, values }: { record?: RecordValues; values: Record<string, unknown> }) =>
      record ? api.update(record, values) : api.create(values),
    onSuccess: () => {
      setEditing(undefined);
      void refresh();
      void queryClient.invalidateQueries({ queryKey: ['reference'] });
    },
  });

  const remove = useMutation({
    mutationFn: (record: RecordValues) => api.remove(record),
    onSuccess: () => {
      setDeleting(undefined);
      void refresh();
    },
  });

  const restore = useMutation({
    mutationFn: (record: RecordValues) => api.restore(record),
    onSuccess: () => {
      setRestoring(undefined);
      void refresh();
      void queryClient.invalidateQueries({ queryKey: ['reference'] });
    },
  });

  const items = list.data?.items ?? [];
  const columns: ListColumn<RecordValues>[] = def.columns
    .filter((column) => !isFixed(column.key))
    .map((column) => ({
      key: column.key,
      header: t(column.header),
      render: (record) => <CellValue column={column} value={record[column.key]} />,
    }));
  const startEditing = (next: { record?: RecordValues; values: Record<string, unknown> }) => {
    save.reset();
    setSubmitted(false);
    setEditing(next);
  };
  const submit = () => {
    if (!editing) return;
    setSubmitted(true);
    if (missingRequired(def, editing.values, Boolean(addressSource)).length === 0) save.mutate(editing);
  };
  const open = (record: RecordValues) =>
    onOpen ? onOpen(record) : canWrite ? startEditing({ record, values: formValues(def, record) }) : undefined;
  const clickable = Boolean(onOpen) || canWrite;
  const saveProblem = save.error ? asProblem(save.error) : undefined;
  // Field errors show on their field; anything else shows in the dialog.
  const saveError =
    saveProblem && !saveProblem.errors && !saveProblem.field ? problemMessage(t, saveProblem) : undefined;

  return (
    <section className={embedded ? 'pos-admin-page pos-admin-page--embedded' : 'pos-admin-page'}>
      {!embedded && <PageHeader title={t(def.title)} />}
      {quickFilters && quickFilters.length > 0 && (
        <div className="pos-admin-page__quick-filters">
          <ContentSwitcher
            size="sm"
            selectedIndex={quickIndex}
            onChange={({ index }) => {
              if (index == null) return;
              filters.applyValues(index === 0 ? {} : (quickFilters[index - 1]?.values ?? {}));
            }}
          >
            {[t('admin.quickFilter.all'), ...quickFilters.map((q) => q.label)].map((label) => (
              <Switch key={label} name={label} text={label} />
            ))}
          </ContentSwitcher>
        </div>
      )}
      <FilterLayout
        filters={filters}
        fields={
          <>
            <ResourceFilters filters={filterDefs} values={filters.draft} onChange={filters.setDraft} />
            <RemovedFilter value={filters.draft[REMOVED] ?? ''} onChange={(v) => filters.set(REMOVED, v)} />
          </>
        }
      >
        <ListTable
          ariaLabel={t(def.title)}
          columns={columns}
          rows={items}
          loading={list.isLoading}
          error={list.error ? problemMessage(t, asProblem(list.error)) : undefined}
          onRetry={() => void list.refetch()}
          search={{
            label: t('admin.search'),
            onChange: (value) => {
              setSearch(value);
              setPage(1);
            },
          }}
          filters={filters}
          toolbarActions={
            <>
              {toolbar?.({ refresh: () => void refresh() })}
              {canWrite && !showRemoved && (
                <Button
                  renderIcon={Add}
                  onClick={() => (onCreate ? onCreate() : startEditing({ values: { ...formValues(def), ...fixed } }))}
                >
                  {t('admin.create')}
                </Button>
              )}
            </>
          }
          onRowClick={clickable && !showRemoved ? open : undefined}
          rowActions={
            !canWrite
              ? undefined
              : showRemoved
                ? (record) => (
                    <RowAction
                      action={t('admin.removed.restore')}
                      record={recordLabel(def, record)}
                      icon={Undo}
                      onClick={() => setRestoring(record)}
                    />
                  )
                : (record) => (
                    <RowAction
                      action={t('admin.delete')}
                      record={recordLabel(def, record)}
                      icon={TrashCan}
                      onClick={() => setDeleting(record)}
                    />
                  )
          }
          empty={
            showRemoved && !search
              ? { heading: t('admin.removed.emptyTitle'), description: t('admin.removed.empty') }
              : !search && filters.count === 0
              ? {
                  heading: t('admin.emptyTitle'),
                  description: canWrite ? t('admin.empty') : undefined,
                }
              : undefined
          }
          pagination={{
            page,
            pageSize,
            pageSizes: PAGE_SIZES,
            total: list.data?.total ?? 0,
            capped: list.data?.totalCapped,
            onChange: ({ page: nextPage, pageSize: nextSize }) => {
              setPage(nextPage);
              setPageSize(nextSize);
            },
          }}
        />
      </FilterLayout>

      {editing && (
        <FormModal
          title={t(editing.record ? 'admin.edit' : 'admin.create')}
          label={t(def.title)}
          pending={save.isPending}
          error={saveError}
          onSubmit={submit}
          onClose={() => {
            save.reset();
            setEditing(undefined);
          }}
        >
          <ResourceForm
            def={formDef}
            values={editing.values}
            existing={Boolean(editing.record)}
            error={save.error}
            onChange={(values) => setEditing({ ...editing, values })}
            onSubmit={submit}
            submitted={submitted}
            bare
          />
        </FormModal>
      )}

      {restoring && (
        <Modal
          open
          preventCloseOnClickOutside
          modalHeading={t('admin.removed.confirm')}
          modalLabel={recordLabel(def, restoring)}
          closeButtonLabel={t('carbon.close')}
          primaryButtonText={t('admin.removed.restore')}
          secondaryButtonText={t('common.cancel')}
          onRequestClose={() => {
            restore.reset();
            setRestoring(undefined);
          }}
          onRequestSubmit={() => restore.mutate(restoring)}
        >
          <p>{t('admin.removed.confirmBody')}</p>
          {restore.error && (
            <InlineNotification
              kind="error"
              role="alert"
              lowContrast
              hideCloseButton
              title={problemMessage(t, asProblem(restore.error))}
            />
          )}
        </Modal>
      )}
      {deleting && (
        <Modal
          open
          danger
          preventCloseOnClickOutside
          modalHeading={t('admin.deleteConfirm')}
          modalLabel={recordLabel(def, deleting)}
          closeButtonLabel={t('carbon.close')}
          primaryButtonText={t('admin.delete')}
          secondaryButtonText={t('common.cancel')}
          onRequestClose={() => {
            remove.reset();
            setDeleting(undefined);
          }}
          onRequestSubmit={() => remove.mutate(deleting)}
        >
          {remove.error && (
            <InlineNotification
              kind="error"
              role="alert"
              lowContrast
              hideCloseButton
              title={problemMessage(t, asProblem(remove.error))}
            />
          )}
        </Modal>
      )}
    </section>
  );
}

/**
 * The fields an address found through the maps provider fills (ADR 0026): the street, the point when the resource has latitude and
 * longitude, the postal code, and the province, district and ward codes whose names match the provider's (in turn, each under the
 * one above). A level without an exact match is left empty for staff to choose, and the levels below it too: never a guess.
 */
async function fillFromAddress(
  def: ResourceDef,
  keys: AddressFieldKeys,
  source: AddressSource,
  values: Record<string, unknown>,
  place: FoundAddress,
): Promise<Record<string, unknown>> {
  const filled: Record<string, unknown> = {
    [keys.street]: place.street?.trim() || place.formattedAddress.split(',')[0]?.trim() || null,
    [keys.province]: null,
    [keys.district]: null,
    [keys.ward]: null,
  };
  if (place.postalCode) filled[keys.postal] = place.postalCode;
  const has = (key: string) => def.fields.some((f) => f.key === key);
  if (has('latitude') && has('longitude')) {
    filled.latitude = Math.round(place.latitude * 1e6) / 1e6;
    filled.longitude = Math.round(place.longitude * 1e6) / 1e6;
  }

  try {
    const countries = await source.countries();
    const country = countries.find((c) => c.code === values[keys.country]) ?? countries[0];
    if (!country) return filled;
    filled[keys.country] = country.code;
    const codeKey = { 1: keys.province, 2: keys.district, 3: keys.ward } as const;
    const nameOf = { 1: place.province, 2: place.district, 3: place.ward } as const;
    let parentId: string | undefined;
    const levels = country.levels
      .filter((l): l is typeof l & { level: 1 | 2 | 3 } => l.level >= 1 && l.level <= 3)
      .sort((a, b) => a.level - b.level);
    for (const [index, l] of levels.entries()) {
      if (index > 0 && !parentId) break;
      const rows = await source.divisions({ country: country.code, level: l.level, parentId, limit: 1000 });
      const match = matchDivision(nameOf[l.level], rows);
      if (!match) break;
      filled[codeKey[l.level]] = match.code;
      parentId = match.id;
    }
  } catch {
    // The lists can't be read: the street and the point are still filled.
  }
  return filled;
}

/**
 * The fields of a resource with server errors shown on the field they belong to. Required fields are marked by the
 * minority rule (`requiredMarking`) and flagged when left empty or once `submitted`. `bare` renders the fields
 * without their own <form>, for a FormModal that provides it; otherwise `id` names the form so a button elsewhere can
 * submit it.
 */
export function ResourceForm({
  def,
  values,
  error,
  onChange,
  onSubmit,
  submitted,
  bare,
  id,
  only,
  existing,
}: {
  def: ResourceDef;
  values: Record<string, unknown>;
  error: unknown;
  onChange(values: Record<string, unknown>): void;
  onSubmit(): void;
  submitted?: boolean;
  bare?: boolean;
  id?: string;
  /** Show only these fields, e.g. one tab's share of the record page; all of them when left out. */
  only?: readonly string[];
  /** Editing a saved record: its `fixedOnEdit` fields are shown but can't be changed. */
  existing?: boolean;
}) {
  const { t } = useTranslation();
  const { addressSource, addressLookup, extensions } = useAdmin();
  const editors = extensions[def.key]?.fieldEditors;
  const problem = asProblem(error);
  // A resource with an address shows one picker for its address columns (ADR 0026), the same one every screen uses.
  const keys = addressSource ? def.address : undefined;
  const addressKeys = keys
    ? new Set([keys.country, keys.province, keys.district, keys.ward, keys.postal, keys.street])
    : undefined;
  const text = (key: string) => (typeof values[key] === 'string' ? (values[key] as string) : '');
  const fieldError = (key: string) => {
    const byName = Object.entries(problem?.errors ?? {}).find(
      ([name]) => name.toLowerCase() === key.toLowerCase(),
    )?.[1]?.[0];
    return byName ?? (problem?.field === key ? problemMessage(t, problem) : undefined);
  };

  const shown = (f: { key: string; hidden?: boolean }) => !f.hidden && (!only || only.includes(f.key));
  const marking = requiredMarking(def.fields.filter((f) => shown(f) && !addressKeys?.has(f.key)));
  const Wrapper = bare ? 'div' : 'form';
  const wrapperProps = bare
    ? {}
    : {
        id,
        noValidate: true,
        onSubmit: (event: React.FormEvent) => {
          event.preventDefault();
          onSubmit();
        },
      };

  return (
    <Wrapper className="pos-admin-form" {...wrapperProps}>
      {def.fields.filter(shown).map((field) =>
        keys && addressKeys?.has(field.key) ? (
          field.key === keys.street && addressSource ? (
            <div key="address" className="pos-admin-address">
              {addressLookup && (
                <AddressLookupField
                  id={`address-lookup-${def.key}`}
                  source={addressLookup}
                  onFound={(place) =>
                    void fillFromAddress(def, keys, addressSource, values, place).then((filled) =>
                      onChange({ ...values, ...filled }),
                    )
                  }
                />
              )}
              <AddressFields
                key="address"
                idPrefix={`address-${def.key}`}
                source={addressSource}
                value={{
                  ...EMPTY_ADDRESS,
                  countryCode: text(keys.country),
                  provinceCode: text(keys.province),
                  districtCode: text(keys.district),
                  wardCode: text(keys.ward),
                  postalCode: text(keys.postal),
                  street: text(keys.street),
                }}
                onChange={(next: AddressValue) =>
                  onChange({
                    ...values,
                    [keys.country]: next.countryCode || null,
                    [keys.province]: next.provinceCode || null,
                    [keys.district]: next.districtCode || null,
                    [keys.ward]: next.wardCode || null,
                    [keys.postal]: next.postalCode || null,
                    [keys.street]: next.street || null,
                  })
                }
              />
            </div>
          ) : null
        ) : editors?.[field.key] ? (
          <div key={field.key}>
            {editors[field.key]?.({
              field,
              value: values[field.key],
              invalidText: fieldError(field.key),
              values,
              onChange: (value) => onChange({ ...values, [field.key]: value }),
            })}
          </div>
        ) : (
          <FieldInput
            key={field.key}
            field={existing && def.fixedOnEdit?.includes(field.key) ? { ...field, helper: 'admin.fixedOnEdit' } : field}
            value={values[field.key]}
            invalidText={fieldError(field.key)}
            marking={marking}
            submitted={submitted}
            disabled={
              Boolean(def.lock && values[def.lock.when] && def.lock.fields.includes(field.key)) ||
              Boolean(existing && def.fixedOnEdit?.includes(field.key))
            }
            values={values}
            onChange={(value) => onChange({ ...values, [field.key]: value })}
          />
        ),
      )}
      {!bare && problem && !problem.errors && !problem.field && (
        <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={problemMessage(t, problem)} />
      )}
    </Wrapper>
  );
}
